import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import {
  translateText,
  breakIntoSentences,
  identifyDifficultVerbs,
} from '@/services/openaiServer';
import { saveArticle } from '@/services/articles';
import { Article, Sentence, SentenceVerb } from '@/types/article';
import { CloudinaryUploadResponse } from '@/services/cloudinary';
import ArticleCreation from '@/components/ArticleCreation';
import SentenceManagement from '@/components/SentenceManagement';
import VerbManagement from '@/components/VerbManagement';
import TranslationEdit from '@/components/TranslationEdit';
import Footer from '@/components/Footer';
import { useAuth } from '@/hooks/useClerkAuth';

interface AdminProps {
  onArticleAdded: (article: Article) => void;
}

const Admin = ({ onArticleAdded }: AdminProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isIdentifyingVerbs, setIsIdentifyingVerbs] = useState(false);

  // Dialog states
  const [showSentenceConfirm, setShowSentenceConfirm] = useState(false);
  const [showVerbEdit, setShowVerbEdit] = useState(false);
  const [showTranslationEdit, setShowTranslationEdit] = useState(false);

  // Current editing states
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  const [currentOriginalLink, setCurrentOriginalLink] = useState<
    string | undefined
  >(undefined);
  const [currentImageData, setCurrentImageData] =
    useState<CloudinaryUploadResponse | null>(null);
  const [sentences, setSentences] = useState<string[]>([]);
  const [sentenceVerbs, setSentenceVerbs] = useState<{
    [index: number]: SentenceVerb[];
  }>({});
  const [translatedSentences, setTranslatedSentences] = useState<Sentence[]>(
    []
  );
  const { user } = useAuth();

  const handlePrepareSentences = (
    title: string,
    description: string,
    germanText: string,
    imageData: CloudinaryUploadResponse | null,
    originalLink?: string
  ) => {
    setCurrentTitle(title);
    setCurrentDescription(description);
    setCurrentOriginalLink(originalLink);
    setCurrentImageData(imageData);

    // Break text into sentences and add title as first sentence
    const sentenceTexts = !description.trim()
      ? [title.trim(), ...breakIntoSentences(germanText)]
      : [title.trim(), description.trim(), ...breakIntoSentences(germanText)];

    if (sentenceTexts.length <= 1) {
      toast({
        title: 'No sentences found',
        description:
          'Could not split text into sentences. Please check your German text.',
        variant: 'destructive',
      });
      return;
    }

    setSentences(sentenceTexts);
    setShowSentenceConfirm(true);
  };

  const handleIdentifyVerbs = async () => {
    setIsIdentifyingVerbs(true);

    // Run identification per-sentence so a single failure doesn't wipe out
    // the whole batch. Track failures so we can warn the user rather than
    // silently producing an article with no difficult words (which was the
    // previous behaviour and the reason `sentence_verbs` stayed empty).
    const verbsMap: { [index: number]: SentenceVerb[] } = {};
    const failedSentences: number[] = [];
    let lastError: unknown = null;

    try {
      for (let i = 0; i < sentences.length; i++) {
        try {
          const response = await identifyDifficultVerbs(
            { sentence: sentences[i] },
            user?.id
          );
          verbsMap[i] = response.verbs ?? [];
        } catch (err) {
          console.error(`Verb identification failed for sentence ${i}:`, err);
          verbsMap[i] = [];
          failedSentences.push(i);
          lastError = err;
        }
      }

      setSentenceVerbs(verbsMap);

      if (failedSentences.length === sentences.length) {
        const message =
          lastError instanceof Error ? lastError.message : 'Unknown error';
        toast({
          title: 'Verb identification failed',
          description: `Could not identify difficult words for any sentence (${message}). Please check your OpenAI key in Supabase and try again.`,
          variant: 'destructive',
        });
        return;
      }

      if (failedSentences.length > 0) {
        toast({
          title: 'Partial verb identification',
          description: `${failedSentences.length} of ${sentences.length} sentences failed. You can still review and edit before continuing.`,
          variant: 'destructive',
        });
      }

      setShowSentenceConfirm(false);
      setShowVerbEdit(true);
    } finally {
      setIsIdentifyingVerbs(false);
    }
  };

  const handleProcessArticle = async () => {
    setIsProcessing(true);

    try {
      // Translate each sentence
      const translatedSentences: Sentence[] = [];

      for (let i = 0; i < sentences.length; i++) {
        const german = sentences[i];

        // Get English and Vietnamese translations in parallel
        const [englishResponse, vietnameseResponse] = await Promise.all([
          translateText(
            {
              text: german,
              targetLanguage: 'english',
            },
            user?.id
          ),
          translateText(
            {
              text: german,
              targetLanguage: 'vietnamese',
            },
            user?.id
          ),
        ]);

        translatedSentences.push({
          id: `temp-sentence-${i + 1}`,
          order: i + 1,
          german,
          english: englishResponse.translatedText,
          vietnamese: vietnameseResponse.translatedText,
          verbs: sentenceVerbs[i] || [],
        });
      }

      setTranslatedSentences(translatedSentences);
      setShowVerbEdit(false);
      setShowTranslationEdit(true);
    } catch (error) {
      console.error('Translation error:', error);

      let errorMessage = t('admin.processingFailedDescription');

      if (error instanceof Error) {
        if (error.message.includes('OpenAI API key')) {
          errorMessage =
            'OpenAI API key not configured. Please check your environment variables.';
        } else if (error.message.includes('authenticated')) {
          errorMessage = 'You must be logged in to create articles.';
        }
      }

      toast({
        title: t('admin.processingFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveArticle = async () => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'User must be authenticated to save articles.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Save article to Supabase
      const savedArticle = await saveArticle(user.id, {
        title: currentTitle.trim(),
        description: currentDescription.trim() || undefined,
        sentences: translatedSentences,
        imageUrl: currentImageData?.secure_url,
        imagePublicId: currentImageData?.public_id,
        originalLink: currentOriginalLink,
      });

      onArticleAdded(savedArticle);

      // Reset form
      setCurrentTitle('');
      setCurrentDescription('');
      setCurrentOriginalLink(undefined);
      setCurrentImageData(null);
      setSentences([]);
      setSentenceVerbs({});
      setTranslatedSentences([]);
      setShowTranslationEdit(false);

      toast({
        title: t('admin.articleCreated'),
        description: t('admin.articleCreatedDescription', {
          count: translatedSentences.length,
        }),
      });
    } catch (error) {
      console.error('Article saving error:', error);

      let errorMessage = t('admin.processingFailedDescription');

      if (error instanceof Error) {
        if (error.message.includes('authenticated')) {
          errorMessage = 'You must be logged in to create articles.';
        }
      }

      toast({
        title: t('admin.processingFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 flex flex-col">
      <div className="container max-w-4xl mx-auto p-6 flex-grow">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-gradient-hero rounded-lg p-6 text-primary-foreground">
            <h1 className="text-3xl font-bold mb-2">{t('admin.title')}</h1>
            <p className="text-primary-foreground/90">{t('admin.subtitle')}</p>
          </div>
        </div>

        {/* Article Creation Form */}
        <ArticleCreation
          onPrepareSentences={handlePrepareSentences}
          isProcessing={isProcessing}
        />

        {/* Dialogs */}
        <SentenceManagement
          open={showSentenceConfirm}
          onOpenChange={setShowSentenceConfirm}
          sentences={sentences}
          onSentencesChange={setSentences}
          onIdentifyVerbs={handleIdentifyVerbs}
          onProcessArticle={handleProcessArticle}
          isProcessing={isProcessing}
          isIdentifyingVerbs={isIdentifyingVerbs}
        />

        <VerbManagement
          open={showVerbEdit}
          onOpenChange={setShowVerbEdit}
          sentences={sentences}
          sentenceVerbs={sentenceVerbs}
          onSentenceVerbsChange={setSentenceVerbs}
          onProcessArticle={handleProcessArticle}
          isProcessing={isProcessing}
        />

        <TranslationEdit
          open={showTranslationEdit}
          onOpenChange={setShowTranslationEdit}
          translatedSentences={translatedSentences}
          onTranslatedSentencesChange={setTranslatedSentences}
          onSave={handleSaveArticle}
        />
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Admin;
